import type { DataPoint } from '../molecules/charts/zoomable';
import type { MetricSeries, MetricUnit } from '@/lib/api/observability';

export type DisplayMetricUnit =
  | 'count'
  | 'percent'
  | 'milliseconds'
  | 'seconds'
  | 'gibibytes'
  | 'cores'
  | 'per_minute';

const gibibyte = 1024 ** 3;

function displayUnit(series: MetricSeries[]): DisplayMetricUnit {
  const unit = series[0]?.unit ?? 'count';

  if (unit === 'bytes') {
    return 'gibibytes';
  }
  if (unit === 'ratio') {
    return 'percent';
  }
  if (
    unit === 'milliseconds' &&
    series.some((metric) =>
      metric.points.some(
        (point) => point.value !== null && Math.abs(point.value) >= 1000,
      ),
    )
  ) {
    return 'seconds';
  }
  return unit;
}

function normalizeValue(value: number, unit: DisplayMetricUnit) {
  if (unit === 'gibibytes') {
    return value / gibibyte;
  }
  if (unit === 'percent') {
    return value * 100;
  }
  if (unit === 'seconds') {
    return value / 1000;
  }
  return value;
}

export function normalizeMetricSeries(
  series: MetricSeries[],
  unit?: DisplayMetricUnit,
) {
  const rawUnits = new Set<MetricUnit>(series.map((metric) => metric.unit));
  if (rawUnits.size > 1) {
    return {
      data: [] as DataPoint<string>[],
      unit: 'count' as DisplayMetricUnit,
      incompatible: true,
      hasValues: false,
    };
  }

  const resolved = unit ?? displayUnit(series);
  const rows = new Map<string, DataPoint<string>>();

  for (const metric of series) {
    for (const point of metric.points) {
      const row =
        rows.get(point.at) ?? ({ date: point.at } as DataPoint<string>);
      row[metric.label] =
        point.value === null ? null : normalizeValue(point.value, resolved);
      rows.set(point.at, row);
    }
  }

  return {
    data: [...rows.values()].sort((left, right) =>
      left.date.localeCompare(right.date),
    ),
    unit: resolved,
    incompatible: false,
    hasValues: series.some((metric) =>
      metric.points.some((point) => point.value !== null),
    ),
  };
}

export function formatCount(value: number, locale?: string) {
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
}

export function formatMetricValue(value: number, unit: DisplayMetricUnit) {
  const formatted = value.toLocaleString(undefined, {
    maximumFractionDigits: unit === 'count' ? 0 : unit === 'per_minute' ? 1 : 2,
  });
  const suffix: Record<DisplayMetricUnit, string> = {
    count: '',
    percent: '%',
    milliseconds: ' ms',
    seconds: ' s',
    gibibytes: ' GiB',
    cores: ' cores',
    per_minute: ' /min',
  };

  return `${formatted}${suffix[unit]}`;
}
